/**
 * usePasskey Hook
 * 
 * Manages passkey (Face ID, Touch ID, Windows Hello) authentication flow.
 * Uses WebAuthn API for biometric authentication.
 * 
 * This is an EXCEPTION hook (per FRONTEND-AI-CONTEXT.md section 8.6):
 * - Auth hooks need complex logic (tokens, context, navigation, WebAuthn)
 * - Uses direct API calls until Orval generates the types
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  startRegistration, 
  startAuthentication,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { api } from '@/core/api/apiClient';
import { useAuth } from '@/core/contexts/AuthContext';
import { track, getAttributionData } from '@/core/tracking/tracking';
import { getRedirectUrl, clearRedirectUrl } from '@/utils/redirect';

// Response types
interface PasskeyItem {
  id: string;
  deviceName: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

interface UsePasskeyResult {
  // State
  isSupported: boolean;
  isLoading: boolean;
  error: string | null;
  passkeys: PasskeyItem[];
  
  // Actions
  checkSupport: () => Promise<boolean>;
  register: (deviceName?: string) => Promise<boolean>;
  login: (email?: string) => Promise<boolean>;
  listPasskeys: () => Promise<PasskeyItem[]>;
  deletePasskey: (id: string) => Promise<boolean>;
  shouldShowPromotion: () => Promise<boolean>;
  dismissPromotion: () => void;
  getDeviceLabel: () => string;
  hasPasskeysOnDevice: () => Promise<boolean>;
  hasRegisteredPasskey: () => boolean; // Check if user has registered a passkey on THIS device
}

// Storage keys
const PASSKEY_DISMISSED_KEY = 'passkey_prompt_dismissed';
const PASSKEY_REGISTERED_KEY = 'passkey_registered_on_device'; // Set when user registers a passkey
const DISMISS_DURATION_DAYS = 30;

/**
 * Get device-specific label for passkey authentication
 */
function getDeviceLabel(): string {
  const userAgent = navigator.userAgent.toLowerCase();
  
  // iOS devices
  if (/iphone|ipad|ipod/.test(userAgent)) {
    // iPhone X and later have Face ID
    const hasNotch = window.screen.height >= 812 && window.devicePixelRatio >= 2;
    return hasNotch ? 'Face ID' : 'Touch ID';
  }
  
  // macOS
  if (/macintosh|mac os x/.test(userAgent)) {
    return 'Touch ID';
  }
  
  // Windows
  if (/windows/.test(userAgent)) {
    return 'Windows Hello';
  }
  
  // Android
  if (/android/.test(userAgent)) {
    return 'Biometría';
  }
  
  return 'Passkey';
}

/**
 * Hook for passkey authentication
 */
export function usePasskey(): UsePasskeyResult {
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passkeys, setPasskeys] = useState<PasskeyItem[]>([]);
  
  // useNavigate can fail outside Router context - make it optional
  let navigate: ReturnType<typeof useNavigate> | null = null;
  try {
    navigate = useNavigate();
  } catch {
    // Not inside Router context - navigation will use window.location
  }
  const { login: loginContext, user } = useAuth();

  // Check WebAuthn support on mount
  useEffect(() => {
    checkSupport();
  }, []);

  /**
   * Check if device supports WebAuthn with platform authenticator
   */
  const checkSupport = useCallback(async (): Promise<boolean> => {
    try {
      if (!window.PublicKeyCredential) {
        setIsSupported(false);
        return false;
      }

      const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      setIsSupported(available);
      return available;
    } catch {
      setIsSupported(false);
      return false;
    }
  }, []);

  /**
   * Register a new passkey for the authenticated user
   */
  const register = useCallback(async (deviceName?: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      // 1. Get registration options from server
      const optionsResponse = await api.post<PublicKeyCredentialCreationOptionsJSON>(
        '/auth/passkey/register/options'
      );

      if (!optionsResponse.data.success || !optionsResponse.data.data) {
        throw new Error(optionsResponse.data.error?.message || 'Failed to get registration options');
      }

      const options = optionsResponse.data.data;

      // 2. Create credential with WebAuthn
      const credential = await startRegistration({ optionsJSON: options });

      // 3. Verify and save on server
      const verifyResponse = await api.post('/auth/passkey/register/verify', {
        credential,
        deviceName: deviceName || getDeviceLabel(),
      });

      if (!verifyResponse.data.success) {
        throw new Error(verifyResponse.data.error?.message || 'Failed to register passkey');
      }

      // Track registration
      const attribution = await getAttributionData();
      await track('PasskeyRegistered', {
        user_id: user?.id,
        device_name: deviceName || getDeviceLabel(),
        ...attribution,
      });

      // Mark that this device has a registered passkey
      localStorage.setItem(PASSKEY_REGISTERED_KEY, 'true');

      // Refresh passkeys list
      await listPasskeys();

      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to register passkey';
      // Handle user cancellation gracefully
      if (message.includes('cancelled') || message.includes('aborted')) {
        setError(null);
        return false;
      }
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  /**
   * Login with passkey
   */
  const login = useCallback(async (email?: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      // 1. Get authentication options from server
      const optionsResponse = await api.post<PublicKeyCredentialRequestOptionsJSON>(
        '/auth/passkey/login/options',
        { email }
      );

      if (!optionsResponse.data.success || !optionsResponse.data.data) {
        const errorMessage = optionsResponse.data.error?.message || 'Failed to get authentication options';
        // Check if error is because no passkeys exist
        if (errorMessage.includes('not_found') || errorMessage.includes('no encontrado')) {
          throw new Error('No tienes passkeys registrados. Por favor, inicia sesión con otro método y registra un passkey desde tu perfil.');
        }
        throw new Error(errorMessage);
      }

      const options = optionsResponse.data.data;

      // Note: If allowCredentials is empty/undefined, WebAuthn will show ALL available
      // passkeys for this RP (discoverable credentials). This is the expected behavior
      // when user doesn't provide email - the browser/device knows which passkeys are available.

      // 2. Authenticate with WebAuthn
      const credential = await startAuthentication({ optionsJSON: options });

      // 3. Verify on server and get tokens
      const verifyResponse = await api.post<{
        accessToken: string;
        refreshToken: string;
        userId: string;
      }>('/auth/passkey/login/verify', { credential });

      if (!verifyResponse.data.success || !verifyResponse.data.data) {
        const errorMessage = verifyResponse.data.error?.message || 'Failed to verify passkey';
        // Check if error is because passkey not found
        if (errorMessage.includes('not_found') || errorMessage.includes('no encontrado')) {
          throw new Error('El passkey no fue encontrado. Por favor, intenta de nuevo o inicia sesión con otro método.');
        }
        throw new Error(errorMessage);
      }

      const { accessToken, refreshToken, userId } = verifyResponse.data.data;

      // Track login
      const attribution = await getAttributionData();
      await track('Login', {
        method: 'passkey',
        user_id: userId,
        device_label: getDeviceLabel(),
        ...attribution,
      });

      // Update auth context
      loginContext(accessToken, refreshToken);

      // Sync localStorage - if we just logged in with passkey, we obviously have one on this device
      localStorage.setItem(PASSKEY_REGISTERED_KEY, 'true');

      // Get redirect URL from query params or sessionStorage, fallback to /profile
      const redirectUrl = getRedirectUrl('/profile');
      clearRedirectUrl();
      
      // Navigate to redirect URL
      if (navigate) {
        navigate(redirectUrl);
      } else {
        window.location.href = redirectUrl;
      }

      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to authenticate';
      // Handle user cancellation gracefully
      if (message.includes('cancelled') || message.includes('aborted') || message.includes('The operation either timed out or was not allowed')) {
        setError(null);
        return false;
      }
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [loginContext, navigate]);

  /**
   * List user's passkeys
   */
  const listPasskeys = useCallback(async (): Promise<PasskeyItem[]> => {
    try {
      const response = await api.get<{ passkeys: PasskeyItem[] }>('/auth/passkey/list');

      if (response.data.success && response.data.data) {
        setPasskeys(response.data.data.passkeys);
        return response.data.data.passkeys;
      }
      return [];
    } catch {
      return [];
    }
  }, []);

  /**
   * Delete a passkey
   */
  const deletePasskey = useCallback(async (id: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await api.delete(`/auth/passkey/${id}`);

      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to delete passkey');
      }

      // Refresh passkeys list
      await listPasskeys();

      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete passkey';
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [listPasskeys]);

  /**
   * Check if we should show the passkey promotion modal
   * Only shows if:
   * 1. User is authenticated (needed to check passkeys)
   * 2. Device is mobile (iOS/Android) - Face ID/Touch ID is most useful there
   * 3. Device supports biometrics
   * 4. User doesn't have any passkeys registered yet (checked via API)
   * 5. User hasn't dismissed in last 30 days
   */
  const shouldShowPromotion = useCallback(async (): Promise<boolean> => {
    // Store rejection reason in sessionStorage for debug panel
    const setRejection = (reason: string) => {
      sessionStorage.setItem('passkey_rejection', reason);
    };
    
    try {
      // 1. User must be authenticated to check passkeys
      if (!user) {
        setRejection('No user');
        return false;
      }

      // 2. Only show on mobile devices (iOS/Android)
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobile = /iphone|ipad|ipod|android/.test(userAgent);
      if (!isMobile) {
        setRejection(`Not mobile: ${userAgent.substring(0, 30)}`);
        return false;
      }

      // 3. Check device support
      const supported = await checkSupport();
      if (!supported) {
        setRejection('WebAuthn not supported');
        return false;
      }

      // 4. Check if user has dismissed recently
      const dismissed = localStorage.getItem(PASSKEY_DISMISSED_KEY);
      if (dismissed) {
        const dismissedDate = new Date(parseInt(dismissed));
        const daysSince = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSince < DISMISS_DURATION_DAYS) {
          setRejection(`Dismissed ${Math.floor(daysSince)}d ago`);
          return false;
        }
      }

      // 5. Check if this device already has a registered passkey (via localStorage flag)
      // We use localStorage because iOS's "silent" mediation still shows a modal
      if (localStorage.getItem(PASSKEY_REGISTERED_KEY) === 'true') {
        setRejection('Already registered on this device (localStorage)');
        return false;
      }

      // 6. Check if user already has passkeys in DB (covers case where localStorage was cleared)
      // This prevents showing promotion modal when user already registered but cleared browser data
      try {
        const response = await api.get<{ id: string }[]>('/auth/passkey/list');
        if (response.data.success && response.data.data && response.data.data.length > 0) {
          // User has passkeys - sync localStorage and don't show promotion
          localStorage.setItem(PASSKEY_REGISTERED_KEY, 'true');
          setRejection(`Already has ${response.data.data.length} passkey(s) in DB`);
          return false;
        }
      } catch {
        // API call failed - continue with promotion (better UX than blocking)
      }

      setRejection('');
      return true;
    } catch (err) {
      setRejection(`Error: ${err}`);
      return false;
    }
  }, [user, checkSupport, listPasskeys]);

  /**
   * Dismiss the promotion modal
   */
  const dismissPromotion = useCallback(() => {
    localStorage.setItem(PASSKEY_DISMISSED_KEY, Date.now().toString());
  }, []);

  /**
   * Check if this device has passkeys registered for this app
   * Uses localStorage flag set during registration - no API calls needed
   */
  const hasPasskeysOnDevice = useCallback(async (): Promise<boolean> => {
    try {
      // Must support WebAuthn
      if (!window.PublicKeyCredential) return false;
      
      // Check localStorage flag - this is set when user registers a passkey on this device
      // This is more reliable than trying "silent" mediation which still shows UI on iOS
      return localStorage.getItem(PASSKEY_REGISTERED_KEY) === 'true';
    } catch {
      return false;
    }
  }, []);

  /**
   * Check if user has registered a passkey on THIS device (via localStorage flag)
   * This is a synchronous check for use in UI rendering
   */
  const hasRegisteredPasskey = useCallback((): boolean => {
    return localStorage.getItem(PASSKEY_REGISTERED_KEY) === 'true';
  }, []);

  return {
    isSupported,
    isLoading,
    error,
    passkeys,
    checkSupport,
    register,
    login,
    listPasskeys,
    deletePasskey,
    shouldShowPromotion,
    dismissPromotion,
    getDeviceLabel,
    hasPasskeysOnDevice,
    hasRegisteredPasskey,
  };
}

export { getDeviceLabel };

